/**
 * History List Component
 * Displays the list of history entries or empty state
 */

import React from 'react';
import { SearchEntry } from '@/store/chatHistoryStore';
import {
  spacing,
  typographyColors,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { HistoryItem } from './item';
import { HistoryLoadingPlaceholder } from './HistoryLoadingPlaceholder';

interface HistoryListProps {
  entries: SearchEntry[];
  searchQuery: string;
  isLoading?: boolean;
  onEntryClick: (index: number) => void;
  onShare?: (index: number) => void;
  onDelete?: (index: number) => void;
}

export const HistoryList: React.FC<HistoryListProps> = ({
  entries,
  searchQuery,
  isLoading = false,
  onEntryClick,
  onShare,
  onDelete,
}) => {
  // Debug: Log what HistoryList receives
  console.log('📋 [HistoryList] Received props:', {
    entriesCount: entries.length,
    isLoading,
    searchQuery,
    hasEntries: entries.length > 0,
    firstEntry: entries[0]
      ? {
          title: entries[0].title,
          conversationId: (
            entries[0] as SearchEntry & { conversationId?: string }
          )?.conversationId,
        }
      : null,
  });

  // Show loading placeholder while API is loading
  if (isLoading) {
    console.log('⏳ [HistoryList] Showing loading placeholder');
    return <HistoryLoadingPlaceholder />;
  }

  // Show empty message only when not loading and no entries
  if (entries.length === 0) {
    console.log('⚠️ [HistoryList] No entries, showing empty state');
    return (
      <div
        style={{
          padding: spacing[8],
          textAlign: 'center',
          color: typographyColors.tertiary,
          fontFamily: fontPrimary,
          fontSize: typography.size.base,
        }}
      >
        {searchQuery
          ? 'No chats found matching your search.'
          : 'No chat history yet. Start a new chat to see your conversations here.'}
      </div>
    );
  }

  console.log('✅ [HistoryList] Rendering entries:', entries.length);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[0],
        width: '100%',
        alignItems: 'flex-start',
      }}
      className='w-full md:w-[761px]'
    >
      {entries.map((entry, index) => {
        console.log(`📝 [HistoryList] Rendering entry ${index}:`, {
          title: entry.title,
          conversationId: (entry as SearchEntry & { conversationId?: string })
            ?.conversationId,
        });
        return (
          <HistoryItem
            key={index}
            entry={entry}
            index={index}
            totalEntries={entries.length}
            onClick={() => onEntryClick(index)}
            onShare={onShare}
            onDelete={onDelete}
          />
        );
      })}
    </div>
  );
};
